import { atom, computed } from 'nanostores';

import { SyncClient, type SyncMode } from '../../sync/client/sync_client.js';
import type { WebSocketClientOptions } from '../../sync/client/websocket_client.js';

const statusHierarchy = new Map([
  ['OFFLINE', 0],
  ['ONLINE', 1],
  ['SYNC', 2],
  ['PRESENTATION', 3],
]);

type SessionOptions = Pick<
  WebSocketClientOptions,
  'serverUrl' | 'sessionId' | 'systemDomainId' | 'userId' | 'systemName'
>;

export function createSyncStore(playerEvents: EventTarget) {
  const $sessionOptions = atom<SessionOptions | undefined>();

  function setSessionOptions(sessionOptions: SessionOptions | undefined) {
    if (sessionOptions === undefined) {
      $sessionOptions.set(undefined);
      return;
    }
    const currentSessionOptions = $sessionOptions.get();
    if (
      currentSessionOptions &&
      currentSessionOptions.sessionId === sessionOptions.sessionId &&
      currentSessionOptions.systemName === sessionOptions.systemName &&
      currentSessionOptions.systemDomainId === sessionOptions.systemDomainId &&
      currentSessionOptions.serverUrl === sessionOptions.serverUrl &&
      currentSessionOptions.userId === sessionOptions.userId
    ) {
      return;
    }
    $sessionOptions.set(sessionOptions);
  }

  const syncClient: SyncClient = new SyncClient(playerEvents);

  function connectSyncClient() {
    const sessionOptions = $sessionOptions.get();
    if (sessionOptions) {
      syncClient.disconnect();
      syncClient.connect({
        serverUrl: sessionOptions.serverUrl,
        sessionId: sessionOptions.sessionId,
        systemDomainId: sessionOptions.systemDomainId,
        userId: sessionOptions.userId,
        systemName: sessionOptions.systemName,
      });
    } else {
      syncClient.disconnect();
    }
  }

  $sessionOptions.listen(connectSyncClient);

  function setSyncMode(mode: SyncMode) {
    syncClient.setSyncMode(mode);
  }

  const $userStatuses = computed(
    [syncClient.$participantStatuses],
    (participantStatuses) => {
      const userStatusValues = Object.values(participantStatuses);
      const userStatusMap: Record<string, SyncMode> = {};
      for (const userStatus of userStatusValues) {
        const currentStatus = userStatusMap[userStatus.userId];
        const currentPriority = statusHierarchy.get(currentStatus) ?? -1;
        const newPriority = statusHierarchy.get(userStatus.status) ?? -1;

        if (newPriority > currentPriority) {
          userStatusMap[userStatus.userId] = userStatus.status;
        }
      }
      return userStatusMap;
    },
  );

  return {
    $sessionOptions,
    $participantStatuses: syncClient.$participantStatuses,
    $sessionSyncMode: syncClient.$sessionSyncMode,
    $userSyncMode: syncClient.$userSyncMode,
    $userStatuses,
    setSessionOptions,
    syncClient,
    setSyncMode,
  };
}
